import React, { useState } from "react"

import {
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
} from "@coval/ui"
import { Hash, Plus, ScrollText, X } from "lucide-react"

import {
  DTMF_MAX_DIGITS,
  isDtmfScriptTurn,
  validateDtmfDigits,
  type ScriptTurn,
} from "@/types/ScriptTurn"
import type { TestCaseInput } from "@/types/TestCaseInput"
import { TestSetType, type TestSetInput } from "@/types/TestSetInput"
import { cn } from "@/utils/cn"

interface ScriptTurnsModalProps {
  onTestCasesProcessed: (testSetInput: TestSetInput) => Promise<void>
  triggerClassName?: string
}

interface DraftTurn {
  kind: "text" | "dtmf"
  text: string
  digits: string
}

function makeDraftTextTurn(text = ""): DraftTurn {
  return { kind: "text", text, digits: "" }
}

function makeDraftDtmfTurn(): DraftTurn {
  return { kind: "dtmf", text: "", digits: "" }
}

function draftToScriptTurn(draft: DraftTurn): ScriptTurn | null {
  if (draft.kind === "dtmf") {
    const digits = draft.digits.trim()
    if (!digits) {
      return null
    }
    return { type: "dtmf", digits }
  }
  const text = draft.text.trim()
  if (!text) {
    return null
  }
  return text
}

const ScriptTurnsModal: React.FC<ScriptTurnsModalProps> = ({
  onTestCasesProcessed,
  triggerClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [turns, setTurns] = useState<DraftTurn[]>([makeDraftTextTurn()])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const resetState = () => {
    setTurns([makeDraftTextTurn()])
    setIsSubmitting(false)
  }

  const updateTurnText = (index: number, value: string) => {
    setTurns((prev) =>
      prev.map((turn, i) => (i === index ? { ...turn, text: value } : turn)),
    )
  }

  const updateTurnDigits = (index: number, value: string) => {
    const sanitized = value.replace(/[^0-9*#]/g, "")
    setTurns((prev) =>
      prev.map((turn, i) =>
        i === index ? { ...turn, digits: sanitized } : turn,
      ),
    )
  }

  const addTextTurn = () => {
    setTurns((prev) => [...prev, makeDraftTextTurn()])
  }

  const addDtmfTurn = () => {
    setTurns((prev) => [...prev, makeDraftDtmfTurn()])
  }

  const removeTurn = (index: number) => {
    setTurns((prev) => prev.filter((_, i) => i !== index))
  }

  const hasValidTurns = turns.some(
    (turn) =>
      (turn.kind === "text" && turn.text.trim().length > 0) ||
      (turn.kind === "dtmf" && turn.digits.trim().length > 0),
  )

  const hasInvalidDtmf = turns.some(
    (turn) =>
      turn.kind === "dtmf" &&
      turn.digits.length > 0 &&
      validateDtmfDigits(turn.digits).error !== null,
  )

  const canSubmit = hasValidTurns && !hasInvalidDtmf

  const handleSubmit = async () => {
    if (!canSubmit) {
      return
    }

    setIsSubmitting(true)
    try {
      const scriptTurns = turns
        .map(draftToScriptTurn)
        .filter((turn): turn is ScriptTurn => turn !== null)

      const inputStr = scriptTurns
        .map((turn, i) => {
          if (isDtmfScriptTurn(turn)) {
            return `Turn ${i + 1}: [DTMF ${turn.digits}]`
          }
          return `Turn ${i + 1}: ${typeof turn === "string" ? turn : ""}`
        })
        .join("\n")

      const testCases: TestCaseInput[] = [
        {
          input_str: inputStr,
          expected_behaviors: [],
          s3_key: null,
          s3_bucket: null,
          input_type: TestSetType.SCRIPT,
          script_turns: scriptTurns,
        },
      ]

      await onTestCasesProcessed({
        display_name: "",
        description: "Scripted turn test cases",
        test_set_metadata: { owner: "" },
        testCases,
        test_set_type: TestSetType.SCRIPT,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Card
        interactive
        className={cn("flex items-center gap-3 px-4 py-3", triggerClassName)}
        onClick={() => setIsOpen(true)}
      >
        <ScrollText className="w-4 h-4 text-coval-text-muted flex-shrink-0" />
        Script a multi-turn conversation
      </Card>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open)
          if (!open) {
            resetState()
          }
        }}
      >
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Create Scripted Turn Test Case</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="space-y-4">
              <p className="text-sm text-coval-text-muted">
                Define what the persona says (or types on the keypad) on each
                turn. Each turn runs in order during the simulation, bypassing
                the LLM.
              </p>

              {turns.map((turn, index) => {
                const validation =
                  turn.kind === "dtmf"
                    ? validateDtmfDigits(turn.digits)
                    : { error: null, warning: null }
                const showError = turn.kind === "dtmf" && turn.digits.length > 0
                return (
                  <div key={index} className="flex items-start gap-2">
                    <span
                      id={`turn-label-${index}`}
                      className="text-sm font-medium text-coval-text-muted mt-2.5 min-w-[52px]"
                    >
                      Turn {index + 1}
                    </span>
                    <div className="flex-1 space-y-1">
                      {turn.kind === "text" ? (
                        <Textarea
                          aria-labelledby={`turn-label-${index}`}
                          placeholder={`What the persona says on turn ${index + 1}...`}
                          value={turn.text}
                          onChange={(e) =>
                            updateTurnText(index, e.target.value)
                          }
                          className="min-h-[56px]"
                        />
                      ) : (
                        <div className="flex items-center gap-2">
                          <Hash
                            className="w-4 h-4 text-coval-text-muted flex-shrink-0"
                            aria-hidden="true"
                          />
                          <Input
                            aria-labelledby={`turn-label-${index}`}
                            placeholder="DTMF digits — e.g. 1, 9658, *#"
                            value={turn.digits}
                            onChange={(e) =>
                              updateTurnDigits(index, e.target.value)
                            }
                            className="font-mono flex-1"
                            inputMode="numeric"
                            autoComplete="off"
                            spellCheck={false}
                          />
                        </div>
                      )}
                      {showError && validation.error && (
                        <p
                          className="text-xs text-coval-danger-text"
                          role="alert"
                          aria-live="polite"
                        >
                          {validation.error}
                        </p>
                      )}
                      {showError && validation.warning && (
                        <p
                          className="text-xs text-coval-warning-text"
                          role="status"
                        >
                          {validation.warning}
                        </p>
                      )}
                    </div>
                    {turns.length > 1 && (
                      <Button
                        icon
                        variant="ghost"
                        className="mt-1.5"
                        onClick={() => removeTurn(index)}
                        tooltip={`Remove turn ${index + 1}`}
                      >
                        <X className="w-4 h-4 text-coval-text-muted" />
                      </Button>
                    )}
                  </div>
                )
              })}

              <div className="flex gap-2">
                <Button variant="secondary" onClick={addTextTurn}>
                  <Plus className="w-4 h-4" />
                  Add Text Turn
                </Button>
                <Button variant="secondary" onClick={addDtmfTurn}>
                  <Hash className="w-4 h-4" />
                  Add DTMF Turn
                </Button>
              </div>
              <p className="text-xs text-coval-text-muted">
                DTMF turns accept digits 0–9, * and #
                {` (up to ~${DTMF_MAX_DIGITS} digits is typical)`}.
              </p>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => {
                setIsOpen(false)
                resetState()
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                void (async () => {
                  try {
                    await handleSubmit()
                    setIsOpen(false)
                    resetState()
                  } catch {
                    // Parent surfaces errors via toast; keep modal open on failure
                  }
                })()
              }}
              disabled={!canSubmit || isSubmitting}
            >
              Add test case
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default ScriptTurnsModal
